import { getTheme } from '../constants/theme';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * ConfirmModal — Modal de confirmation accessible.
 *
 * A11y (D55, sprint UI Pro Vét V1.0) :
 *   - role="dialog" + aria-modal="true" + aria-labelledby (si title).
 *   - Focus initial sur le bouton de confirmation à l'ouverture.
 *   - Focus trap : Tab / Shift+Tab cycle dans la modale, jamais hors.
 *   - Escape ferme via onCancel.
 *   - Mémorisation de l'élément déclencheur à l'ouverture, restitution
 *     du focus à la fermeture (UX clavier sans saut).
 */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function focusableIn(container) {
  if (!container) return [];
  // À l'intérieur d'une modale ouverte, on considère tout élément matching
  // comme focusable. Pas de filtre offsetParent (jsdom-hostile et inutile
  // ici puisque la modale est forcément visible quand isOpen=true).
  return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR))
    .filter((el) => !el.hasAttribute('disabled'));
}

const ConfirmModal = ({
  isOpen,
  title,
  message,
  onConfirm,
  onCancel,
  darkMode = false,
  confirmText,
  cancelText,
  confirmColor = '#EF4444',
}) => {
  const t = getTheme(darkMode);
  // i18n aliasé en `tr` (collision avec le thème `t`). Défauts rétro-compatibles :
  // un call-site qui passe confirmText/cancelText garde priorité.
  const { t: tr } = useTranslation();
  const resolvedConfirm = confirmText ?? tr('common.confirm');
  const resolvedCancel  = cancelText  ?? tr('common.cancel');
  const confirmRef = useRef(null);
  const containerRef = useRef(null);
  const restoreFocusRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    // Mémorise l'élément déclencheur pour restitution à la fermeture.
    restoreFocusRef.current = (typeof document !== 'undefined') ? document.activeElement : null;

    // Focus initial sur le bouton de confirmation.
    confirmRef.current?.focus();

    function handleKey(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
        return;
      }
      if (e.key !== 'Tab') return;

      const container = containerRef.current;
      const items = focusableIn(container);
      if (items.length === 0) {
        e.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;

      if (e.shiftKey) {
        if (active === first || !container.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (active === last || !container.contains(active)) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('keydown', handleKey);
      // Restitution focus à l'élément déclencheur (si toujours focusable).
      const el = restoreFocusRef.current;
      if (el && typeof el.focus === 'function' && document.contains(el)) {
        try { el.focus(); } catch { /* ignore */ }
      }
      restoreFocusRef.current = null;
    };
  }, [isOpen, onCancel]);

  if (!isOpen) return null;

  return (
    <div
      ref={containerRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'confirm-modal-title' : undefined}
      style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0, 0, 0, 0.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 10000, padding: '20px'
      }}>
      <div style={{
        background: t.card,
        borderRadius: '12px',
        maxWidth: '500px',
        width: '100%',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
        overflow: 'hidden'
      }}>
        {title && (
          <div style={{
            padding: '20px 24px',
            borderBottom: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid #e5e7eb',
            background: darkMode ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.05)'
          }}>
            <h3 id="confirm-modal-title" style={{
              margin: 0,
              fontSize: '18px',
              fontWeight: '600',
              color: t.textPrimary,
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              {title}
            </h3>
          </div>
        )}

        <div style={{
          padding: '24px',
          color: t.textSecondary,
          fontSize: '15px',
          lineHeight: '1.6',
          whiteSpace: 'pre-line'
        }}>
          {message}
        </div>

        <div style={{
          padding: '16px 24px',
          borderTop: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid #e5e7eb',
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={onCancel}
            style={{
              padding: '10px 20px',
              borderRadius: '6px',
              border: darkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid #ddd',
              background: 'transparent',
              color: t.textSecondary,
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = darkMode ? 'rgba(255,255,255,0.05)' : '#f3f4f6';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
          >
            {resolvedCancel}
          </button>
          <button
            ref={confirmRef}
            onClick={onConfirm}
            style={{
              padding: '10px 20px',
              borderRadius: '6px',
              border: 'none',
              background: confirmColor,
              color: '#fff',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.9';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            {resolvedConfirm}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
